// orderSlice.js
import { createSlice } from "@reduxjs/toolkit";

// ------------------------------------------------------------
// Load and persist helpers
// ------------------------------------------------------------
const loadLocal = () => {
  try {
    return JSON.parse(localStorage.getItem("cartOrders")) || {};
  } catch {
    return {};
  }
};

const persistUnplaced = (orderData) => {
  const unplaced = {};
  for (const [key, order] of Object.entries(orderData)) {
    const onlyUnplaced = (order?.items || []).filter((it) => typeof it.id === 'undefined');
    if (onlyUnplaced.length) {
      unplaced[key] = {
        tableId: order?.tableId ?? (key !== "temp" ? key : null),
        remarks: order?.remarks || "",
        items: onlyUnplaced,
      };
    }
  }
  localStorage.setItem("cartOrders", JSON.stringify(unplaced));
};

// ------------------------------------------------------------
// Initial state
// ------------------------------------------------------------
const initialState = {
  orderData: loadLocal(), // only unplaced persisted
  orderId: null,
};

// ------------------------------------------------------------
// Slice
// ------------------------------------------------------------
const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    // --------------------------------------------------------
    // 1️⃣ Generic merge for any key (including "temp")
    // --------------------------------------------------------
    setOrderData: (state, action) => {
      for (const [key, incoming] of Object.entries(action.payload)) {
        const existing = state.orderData[key] || { items: [] };
        state.orderData[key] = {
          ...existing,
          ...incoming,
          items: Array.isArray(incoming.items)
            ? incoming.items
            : existing.items,
        };
      }
      // Persist only unplaced items
      persistUnplaced(state.orderData);
    },

    // --------------------------------------------------------
    // 2️⃣ Set API order (backend fetched data)
    // ⚠️ Do NOT persist placed items to localStorage
    // --------------------------------------------------------
    setApiOrderData: (state, action) => {
      for (const [tableId, incoming] of Object.entries(action.payload)) {
        const existing = state.orderData[tableId] || { items: [] };
        state.orderData[tableId] = {
          ...existing,
          ...incoming,
          items: Array.isArray(incoming.items)
            ? incoming.items
            : existing.items,
        };
      }

      // ✅ Only persist unplaced items, skip backend placed ones
      const onlyUnplaced = {};
      for (const [key, order] of Object.entries(state.orderData)) {
        const items = (order?.items || []).filter((it) => typeof it.id === 'undefined');
        if (items.length) {
          onlyUnplaced[key] = {
            tableId: order?.tableId ?? (key !== "temp" ? key : null),
            remarks: order?.remarks || "",
            items,
          };
        }
      }
      localStorage.setItem("cartOrders", JSON.stringify(onlyUnplaced));
    },

    // --------------------------------------------------------
    // 3️⃣ Move temp items into selected table (append/merge)
    // --------------------------------------------------------
    transferTempToTable: (state, action) => {
      const tableId = action.payload;
      const tempOrder = state.orderData.temp;
      if (!tempOrder?.items?.length) return;

      if (!state.orderData[tableId]) {
        state.orderData[tableId] = { tableId, items: [], orderId: null };
      }

      const existingItems = state.orderData[tableId].items || [];
      const tempItems = tempOrder.items;

      tempItems.forEach((tempItem) => {
        const existingIndex = existingItems.findIndex(
          (item) => item.productId === tempItem.productId
        );
        if (existingIndex >= 0) {
          existingItems[existingIndex].quantity += tempItem.quantity;
        } else {
          existingItems.push(tempItem);
        }
      });

      state.orderData[tableId].isNewItem = true;
      state.orderData[tableId].items = existingItems;

      delete state.orderData.temp;
      persistUnplaced(state.orderData);
    },

    // --------------------------------------------------------
    // 4️⃣ Add single item
    // --------------------------------------------------------
    addOrderItem: (state, action) => {
      const { tableId, newItem } = action.payload;
      const bucket = state.orderData[tableId] || {
        tableId,
        remarks: "",
        items: [],
      };
      bucket.items = [...(bucket.items || []), newItem];
      state.orderData[tableId] = bucket;
      persistUnplaced(state.orderData);
    },

    // --------------------------------------------------------
    // 5️⃣ Set current order ID
    // --------------------------------------------------------
    setOrderId: (state, action) => {
      state.orderId = action.payload;
    },

    // --------------------------------------------------------
    // 6️⃣ Reset all orders (global clear)
    // --------------------------------------------------------
    resetOrder: (state) => {
      state.orderData = {};
      state.orderId = null;
      localStorage.removeItem("cartOrders");
    },

    // --------------------------------------------------------
    // 7️⃣ Reset a specific table’s order (after placing)
    // --------------------------------------------------------
    resetTableOrder: (state, action) => {
      const tableId = action.payload;
      delete state.orderData[tableId];
      persistUnplaced(state.orderData);
    },

    // --------------------------------------------------------
    // 8️⃣ Delete specific order items
    // --------------------------------------------------------
    deleteOrderItems: (state, action) => {
      const { tableId, itemIndexes } = action.payload;
      const bucket = state.orderData[tableId];
      if (!bucket) return;

      const sorted = [...itemIndexes].sort((a, b) => b.itemIndex - a.itemIndex);
      sorted.forEach(({ itemIndex }) => {
        bucket.items.splice(itemIndex, 1);
      });
      persistUnplaced(state.orderData);
    },

    // --------------------------------------------------------
    // 9️⃣ Delete only unplaced (not sent) items
    // --------------------------------------------------------
    deleteUnplacedOrderItems: (state, action) => {
      const { tableId, itemIndexes } = action.payload;
      const bucket = state.orderData[tableId];
      if (!bucket) return;

      const sorted = [...itemIndexes].sort((a, b) => b.itemIndex - a.itemIndex);
      sorted.forEach(({ itemIndex }) => {
        const it = bucket.items[itemIndex];
        if (it && typeof it.id === 'undefined') bucket.items.splice(itemIndex, 1);
      });

      if (!bucket.items.length) delete state.orderData[tableId];
      persistUnplaced(state.orderData);
    },
  },
});

// ------------------------------------------------------------
// Exports
// ------------------------------------------------------------
export const {
  setOrderData,
  setApiOrderData,
  addOrderItem,
  setOrderId,
  resetOrder,
  resetTableOrder,
  deleteOrderItems,
  deleteUnplacedOrderItems,
  transferTempToTable,
} = orderSlice.actions;

export default orderSlice.reducer;
